import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip as ChartTooltip,
} from 'chart.js';
import GroupsIcon from '@mui/icons-material/Groups';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DownloadIcon from '@mui/icons-material/Download';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { masteryTone, orchard } from '../../theme';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineController,
  LineElement,
  PointElement,
  Legend,
  ChartTooltip
);

// One colour per child (charts, heat-map header)
const PALETTE = [
  '#C2233E',
  '#2E7D5B',
  '#5B5BD6',
  '#E8A93D',
  '#0E8FA8',
  '#B04ACF',
  '#7A5C3E',
  '#E5603A',
  '#3C8D2F',
  '#9A8FA6',
];
const colorOf = (i) => PALETTE[i % PALETTE.length];
const short = (name) => {
  const p = String(name || '').split(/\s+/);
  return p.length > 1 ? `${p[1]} ${p[0][0]}.` : p[0];
};
const fmtDay = (d, en) =>
  d
    ? new Date(d).toLocaleDateString(en ? 'en-GB' : 'ru-RU', {
        day: 'numeric',
        month: 'short',
      })
    : '—';
const SCORE = {
  2: { bg: orchard.leaf, fg: '#fff', ru: 'знает', en: 'knows' },
  1: { bg: orchard.honey, fg: '#3b2600', ru: 'частично', en: 'partly' },
  0: { bg: '#F3C9D0', fg: orchard.cherryDeep, ru: 'пока нет', en: 'not yet' },
};

function Tile({ label, value, sub, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, flex: '1 1 160px' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: orchard.fontDisplay,
          fontWeight: 600,
          fontSize: '1.7rem',
          lineHeight: 1.2,
          color: color || 'text.primary',
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

function Section({ icon, title, sub, children, action }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.75, sm: 2.5 }, borderRadius: 3, mb: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        {icon && <Box sx={{ color: 'primary.main', mt: '2px' }}>{icon}</Box>}
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

/* «Сравнение»: any children against each other — only on the same level
   (template) and only on the units every one of them went through — plus
   the group as a whole (the collective). Data: GET /api/analytics/compare. */
export default function CompareView({ en, seed, onExport }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState([]); // student ids
  const [templateId, setTemplateId] = useState('');
  const [units, setUnits] = useState(null); // null = server default (common units)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortItems, setSortItems] = useState('order');
  // Long levels (e.g. 377 words): units start folded into one summary row each
  const [openUnits, setOpenUnits] = useState(null); // null = default
  const [allRows, setAllRows] = useState(false);
  const [moreChips, setMoreChips] = useState({});
  useEffect(() => {
    setOpenUnits(null);
    setAllRows(false);
    setMoreChips({});
  }, [data]);
  const unitOpen = (key) =>
    openUnits === null
      ? data
        ? data.items.length <= 60
        : true
      : openUnits.has(key);
  const toggleUnitRow = (key) => {
    const cur =
      openUnits === null
        ? new Set(
            data && data.items.length <= 60
              ? data.items.map((it) => it.unitKey)
              : []
          )
        : new Set(openUnits);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    setOpenUnits(cur);
  };
  const chipList = (list, id, bg) => {
    const shownChips = moreChips[id] ? list : list.slice(0, 14);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {shownChips.map((t) => (
          <Chip key={t} size="small" label={t} sx={{ bgcolor: bg }} />
        ))}
        {list.length > shownChips.length && (
          <Chip
            size="small"
            variant="outlined"
            label={
              en
                ? `+${list.length - shownChips.length} more`
                : `ещё ${list.length - shownChips.length}`
            }
            onClick={() => setMoreChips((m) => ({ ...m, [id]: true }))}
          />
        )}
      </Box>
    );
  };

  useEffect(() => {
    fetch('/api/students', { credentials: 'include' })
      .then((r) => r.json())
      .then((list) =>
        setStudents(
          (Array.isArray(list) ? list : [])
            .map((s) => ({ _id: s._id, name: s.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      )
      .catch(() => {});
    fetch('/api/analytics/groups', { credentials: 'include' })
      .then((r) => r.json())
      .then((g) => setGroups(Array.isArray(g) ? g : []))
      .catch(() => {});
  }, []);

  // Pre-selection from «Недавно оценённые»
  useEffect(() => {
    if (!seed || !seed.studentIds || !seed.studentIds.length) return;
    setPicked(seed.studentIds);
    setTemplateId(seed.templateId || '');
    setUnits(null);
  }, [seed]);

  useEffect(() => {
    if (picked.length < 1) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const q = new URLSearchParams({ students: picked.join(',') });
    if (templateId) q.set('template', templateId);
    if (units && units.length) q.set('units', units.join(','));
    fetch(`/api/analytics/compare?${q}`, { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`);
        return d;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [picked, templateId, units]);

  const byId = useMemo(
    () => new Map(students.map((s) => [s._id, s])),
    [students]
  );
  const colorIdx = useMemo(() => {
    const m = new Map();
    (data ? data.students : []).forEach((s, i) => m.set(s._id, i));
    return m;
  }, [data]);

  const addGroup = (g) => {
    setPicked((p) => [...new Set([...p, ...g.studentIds])]);
    setUnits(null);
  };

  const toggleUnit = (key) => {
    const cur = units || (data ? data.selectedUnits : []);
    const next = cur.includes(key)
      ? cur.filter((k) => k !== key)
      : [...cur, key];
    setUnits(next.length ? next : null);
  };

  const items = useMemo(() => {
    if (!data) return [];
    const list = [...data.items];
    if (sortItems === 'weak')
      list.sort((a, b) => (a.collective ?? 0) - (b.collective ?? 0));
    if (sortItems === 'split')
      list.sort((a, b) => {
        const spread = (it) => {
          const v = Object.values(it.scores).filter((x) => x !== null);
          return v.length ? Math.max(...v) - Math.min(...v) : 0;
        };
        return spread(b) - spread(a);
      });
    return list;
  }, [data, sortItems]);

  const exportCsv = () => {
    if (!data) return;
    const head = [
      en ? 'Unit' : 'Раздел',
      en ? 'Item' : 'Пункт',
      ...data.students.map((s) => s.name),
      en ? 'Group %' : 'Группа %',
    ];
    const rows = data.items.map((it) => [
      it.unit,
      it.text,
      ...data.students.map((s) =>
        it.scores[s._id] === null || it.scores[s._id] === undefined
          ? ''
          : it.scores[s._id]
      ),
      it.collective ?? '',
    ]);
    const csv = [head, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(
      new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `compare_${(data.template && data.template.name) || 'level'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const selectedUnits = data ? data.units.filter((u) => u.selected) : [];
  const unitChart = data && selectedUnits.length > 0 && (
    <Bar
      data={{
        labels: selectedUnits.map((u) =>
          u.name.replace(/^(Глава \d+) · /, '$1: ').slice(0, 40)
        ),
        datasets: [
          ...(data.students.length <= 10
            ? data.students.map((s, i) => ({
                label: s.name,
                data: selectedUnits.map((u) =>
                  s.units[u.key] ? s.units[u.key].pct : null
                ),
                backgroundColor: alpha(colorOf(i), 0.8),
                borderRadius: 4,
              }))
            : []),
          {
            label: en ? 'Group' : 'Вся группа',
            data: selectedUnits.map((u) => u.collective),
            backgroundColor: dark
              ? 'rgba(255,255,255,0.35)'
              : 'rgba(36,30,43,0.25)',
            borderColor: dark ? '#fff' : orchard.ink,
            borderWidth: 1.5,
            borderRadius: 4,
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } },
        },
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
      }}
    />
  );

  const historyChart = useMemo(() => {
    if (!data) return null;
    const withPoints = data.history.filter((h) => h.points.length > 0);
    if (!withPoints.some((h) => h.points.length > 1)) return null;
    const days = [
      ...new Set(
        withPoints.flatMap((h) =>
          h.points.map((p) => String(p.date).slice(0, 10))
        )
      ),
    ].sort();
    return (
      <Line
        data={{
          labels: days.map((d) => fmtDay(d, en)),
          datasets: withPoints.map((h) => {
            const i = colorIdx.get(h._id) ?? 0;
            const map = new Map(
              h.points.map((p) => [String(p.date).slice(0, 10), p.pct])
            );
            return {
              label: h.name,
              data: days.map((d) => (map.has(d) ? map.get(d) : null)),
              borderColor: colorOf(i),
              backgroundColor: colorOf(i),
              spanGaps: true,
              tension: 0.25,
            };
          }),
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } },
          },
          plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }}
      />
    );
  }, [data, colorIdx, en]);

  return (
    <Box>
      {/* ── Who and on what ── */}
      <Section
        icon={<CompareArrowsIcon />}
        title={en ? 'Compare children' : 'Сравнить детей'}
        sub={
          en
            ? 'Only the same level and only the units every child went through are compared — fair for everyone.'
            : 'Сравниваются только один уровень и только разделы, которые прошли все выбранные дети — честно для каждого.'
        }
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={students}
          value={picked.map((id) => byId.get(id) || { _id: id, name: '…' })}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a._id === b._id}
          onChange={(e, v) => {
            setPicked(v.map((s) => s._id));
            setUnits(null);
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Checkbox
                  size="small"
                  checked={selected}
                  sx={{ mr: 1, p: 0.5 }}
                />
                {option.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={en ? 'Children' : 'Дети'}
              placeholder={en ? 'Type a name…' : 'Начните вводить имя…'}
            />
          )}
          ChipProps={{ size: 'small' }}
        />
        {groups.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
              mt: 1.25,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, mr: 0.5 }}
            >
              <GroupsIcon
                sx={{ fontSize: 16, verticalAlign: '-3px', mr: 0.5 }}
              />
              {en ? 'Add a whole group:' : 'Добавить всю группу:'}
            </Typography>
            {groups.map((g) => (
              <Chip
                key={g._id}
                size="small"
                variant="outlined"
                label={`${g.name}${g.mine ? '' : ` · ${g.teacher}`} · ${g.studentIds.length}`}
                onClick={() => addGroup(g)}
                disabled={!g.studentIds.length}
              />
            ))}
            {picked.length > 0 && (
              <Button size="small" onClick={() => setPicked([])}>
                {en ? 'Clear' : 'Очистить'}
              </Button>
            )}
          </Box>
        )}

        {data && data.templates && data.templates.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {en ? 'Level' : 'Уровень'}
            </Typography>
            <Select
              fullWidth
              size="small"
              value={data.template ? data.template._id : ''}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setUnits(null);
              }}
              sx={{ mt: 0.5 }}
            >
              {data.templates.map((t) => (
                <MenuItem key={t._id} value={t._id}>
                  {t.name}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {en
                      ? `${t.students} of ${picked.length} children assessed`
                      : `оценено ${t.students} из ${picked.length}`}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {data && data.units && data.units.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {en
                ? 'Units to compare (tap to add or remove)'
                : 'Разделы для сравнения (нажмите, чтобы добавить или убрать)'}
            </Typography>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}
            >
              {data.units.map((u) => (
                <Tooltip
                  key={u.key}
                  title={
                    u.common
                      ? en
                        ? 'Every child went through this unit'
                        : 'Этот раздел прошли все'
                      : en
                        ? `Only ${u.assessedBy} of ${data.students.length} went through it`
                        : `Прошли только ${u.assessedBy} из ${data.students.length}`
                  }
                >
                  <Chip
                    size="small"
                    label={
                      u.common
                        ? u.name
                        : `${u.name} · ${u.assessedBy}/${data.students.length}`
                    }
                    color={u.selected ? 'primary' : 'default'}
                    variant={u.selected ? 'filled' : 'outlined'}
                    onClick={() => toggleUnit(u.key)}
                    sx={{ borderStyle: u.common ? 'solid' : 'dashed' }}
                  />
                </Tooltip>
              ))}
            </Box>
            {data.commonUnits.length === 0 && (
              <Alert severity="info" sx={{ mt: 1.25 }}>
                {en
                  ? 'These children have no unit in common yet. Pick units above: each is compared among the children who did it.'
                  : 'Общих разделов у этих детей пока нет. Выберите разделы выше — каждый сравнивается среди тех, кто его прошёл.'}
              </Alert>
            )}
          </Box>
        )}
      </Section>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!picked.length && (
        <Alert severity="info" icon={<CompareArrowsIcon />} sx={{ mb: 2 }}>
          {en
            ? 'Pick two or more children (or a whole group) to compare them.'
            : 'Выберите двух или больше детей (или всю группу), чтобы сравнить их.'}
        </Alert>
      )}
      {data && data.templates && data.templates.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {en
            ? 'None of these children has an assessment yet.'
            : 'Ни у кого из выбранных детей пока нет оценок.'}
        </Alert>
      )}

      {data && data.template && (
        <>
          {/* ── The group as a whole ── */}
          <Stack direction="row" sx={{ gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <Tile
              label={en ? 'Children compared' : 'Сравниваем детей'}
              value={data.students.length}
              sub={
                data.notAssessed.length
                  ? en
                    ? `${data.notAssessed.length} not assessed on this level`
                    : `ещё ${data.notAssessed.length} без оценки на этом уровне`
                  : data.template.name
              }
            />
            <Tile
              label={en ? 'Units compared' : 'Разделов в сравнении'}
              value={data.collective.units}
              sub={
                en
                  ? `${data.commonUnits.length} done by everyone`
                  : `${data.commonUnits.length} прошли все`
              }
            />
            <Tile
              label={en ? 'Group result' : 'Результат группы'}
              value={
                data.collective.pct == null ? '—' : `${data.collective.pct}%`
              }
              color={
                data.collective.pct == null
                  ? undefined
                  : masteryTone(data.collective.pct, true, dark)
              }
              sub={
                en
                  ? `${data.collective.known} of ${data.collective.cells} answers “knows”`
                  : `«знает» — ${data.collective.known} из ${data.collective.cells} ответов`
              }
            />
            <Tile
              label={en ? 'Spread' : 'Разброс'}
              value={
                data.collective.min == null
                  ? '—'
                  : `${data.collective.min}–${data.collective.max}%`
              }
              sub={en ? 'weakest – strongest' : 'от слабого до сильного'}
            />
          </Stack>

          {/* ── Ranking against the group ── */}
          <Section
            icon={<EmojiEventsIcon />}
            title={
              en
                ? 'Each child against the group'
                : 'Каждый ребёнок и вся группа'
            }
            sub={
              en
                ? 'Result on the compared units; the badge shows the gap to the group result.'
                : 'Результат по выбранным разделам; значок — разница с результатом группы.'
            }
            action={
              onExport && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    onExport(
                      data.students.map((s) => s._id),
                      data.template._id
                    )
                  }
                >
                  {en ? 'Reports' : 'Отчёты'}
                </Button>
              )
            }
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: 1,
              }}
            >
              {data.students.map((s, i) => (
                <Box
                  key={s._id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 13,
                      fontWeight: 700,
                      bgcolor: colorOf(colorIdx.get(s._id) ?? i),
                    }}
                  >
                    {s.rank || '–'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{ fontWeight: 600, fontSize: '0.92rem' }}
                      >
                        {s.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0 }}
                      >
                        {fmtDay(s.lastAssessed, en)}
                        {s.unitsCovered < data.collective.units
                          ? ` · ${s.unitsCovered}/${data.collective.units}`
                          : ''}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={s.overall || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.text.primary, 0.07),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: masteryTone(
                            s.overall || 0,
                            s.overall != null,
                            dark
                          ),
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{ width: 44, textAlign: 'right', fontWeight: 700 }}
                  >
                    {s.overall == null ? '—' : `${s.overall}%`}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      s.vsCollective == null
                        ? '—'
                        : `${s.vsCollective > 0 ? '+' : ''}${s.vsCollective}`
                    }
                    sx={{
                      width: 52,
                      fontWeight: 700,
                      bgcolor:
                        s.vsCollective > 4
                          ? alpha(orchard.leaf, 0.15)
                          : s.vsCollective < -4
                            ? alpha(orchard.cherry, 0.12)
                            : 'action.hover',
                      color:
                        s.vsCollective > 4
                          ? orchard.leaf
                          : s.vsCollective < -4
                            ? orchard.cherry
                            : 'text.secondary',
                    }}
                  />
                </Box>
              ))}
            </Box>
            {data.notAssessed.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1.5 }}
              >
                {en
                  ? 'Not assessed on this level: '
                  : 'Нет оценки на этом уровне: '}
                {data.notAssessed.map((s) => s.name).join(', ')}
              </Typography>
            )}
          </Section>

          {/* ── Units ── */}
          {selectedUnits.length > 0 && (
            <Section
              title={en ? 'Unit by unit' : 'По разделам'}
              sub={
                en
                  ? 'Each child and the whole group, per unit.'
                  : 'Каждый ребёнок и вся группа по каждому разделу.'
              }
            >
              <Box
                sx={{
                  height: Math.max(
                    260,
                    Math.min(420, 120 + selectedUnits.length * 40)
                  ),
                }}
              >
                {unitChart}
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  gap: 0.5,
                  mt: 1.5,
                }}
              >
                {selectedUnits.map((u) => (
                  <Typography
                    key={u.key}
                    variant="caption"
                    color="text.secondary"
                  >
                    <b>{u.name}</b>: {en ? 'group' : 'группа'}{' '}
                    {u.collective ?? '—'}%
                    {u.best && u.best.pct !== u.weakest.pct
                      ? en
                        ? ` · best ${short(u.best.name)} ${u.best.pct}% · needs help ${short(u.weakest.name)} ${u.weakest.pct}%`
                        : ` · лучше всех ${short(u.best.name)} ${u.best.pct}% · нужна помощь ${short(u.weakest.name)} ${u.weakest.pct}%`
                      : ''}
                  </Typography>
                ))}
              </Box>
            </Section>
          )}

          {/* ── What to do with it ── */}
          <Section
            icon={<LightbulbOutlinedIcon />}
            title={en ? 'What this means' : 'Что из этого следует'}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: 1.5,
              }}
            >
              {data.insights.nobodyYet.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: orchard.cherry }}
                  >
                    {en
                      ? 'Nobody knows yet — teach to everyone:'
                      : 'Пока не знает никто — объяснить всей группе:'}
                  </Typography>
                  {chipList(
                    data.insights.nobodyYet,
                    'nobody',
                    alpha(orchard.cherry, 0.1)
                  )}
                </Box>
              )}
              {data.insights.peerTeaching.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: '#5B5BD6' }}
                  >
                    {en
                      ? 'Some know it, some don’t — pair them up:'
                      : 'Одни знают, другие нет — объединить в пары:'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr)',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {data.insights.peerTeaching.map((p) => (
                      <Typography key={p.text} variant="body2">
                        <b>{p.text}</b> — {en ? 'knows' : 'знают'}:{' '}
                        {p.know.map(short).join(', ')};{' '}
                        {en ? 'not yet' : 'пока нет'}:{' '}
                        {p.notYet.map(short).join(', ')}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {data.insights.knownByAll.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: orchard.leaf }}
                  >
                    {en
                      ? 'Everyone knows — move on:'
                      : 'Знают все — можно идти дальше:'}
                  </Typography>
                  {chipList(
                    data.insights.knownByAll,
                    'all',
                    alpha(orchard.leaf, 0.12)
                  )}
                </Box>
              )}
              {data.insights.perStudent
                .filter(
                  (p) => p.ahead.length || p.behind.length || p.alone.length
                )
                .map((p) => (
                  <Typography key={p._id} variant="body2">
                    <b>{p.name}</b>
                    {p.ahead.length
                      ? `${en ? ' — ahead in ' : ' — впереди: '}${p.ahead.join(', ')}`
                      : ''}
                    {p.behind.length
                      ? `${en ? '; behind in ' : '; отстаёт: '}${p.behind.join(', ')}`
                      : ''}
                    {p.alone.length
                      ? `${en ? '; the only one who doesn’t know yet: ' : '; единственный(ая), кто пока не знает: '}${p.alone.join(', ')}`
                      : ''}
                  </Typography>
                ))}
              {!data.insights.nobodyYet.length &&
                !data.insights.peerTeaching.length &&
                !data.insights.knownByAll.length && (
                  <Typography variant="body2" color="text.secondary">
                    {en
                      ? 'Pick at least two children assessed on the same items.'
                      : 'Выберите хотя бы двух детей, оценённых по одним и тем же пунктам.'}
                  </Typography>
                )}
            </Box>
          </Section>

          {/* ── Who knows what ── */}
          {items.length > 0 && (
            <Section
              title={en ? 'Who knows what' : 'Кто что знает'}
              sub={
                en
                  ? 'Every word or skill of the compared units. Last column: the group.'
                  : 'Каждое слово или навык выбранных разделов. Последний столбец — вся группа.'
              }
              action={
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={sortItems}
                    onChange={(e, v) => v && setSortItems(v)}
                  >
                    <ToggleButton value="order">
                      {en ? 'In order' : 'По порядку'}
                    </ToggleButton>
                    <ToggleButton value="weak">
                      {en ? 'Weakest' : 'Слабые'}
                    </ToggleButton>
                    <ToggleButton value="split">
                      {en ? 'Most different' : 'Разные'}
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportCsv}
                  >
                    CSV
                  </Button>
                </Box>
              }
            >
              <Box sx={{ overflowX: 'auto', mx: { xs: -1.75, sm: 0 } }}>
                <Box
                  component="table"
                  sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '3px',
                    minWidth: '100%',
                    fontSize: '0.8rem',
                  }}
                >
                  <thead>
                    <tr>
                      <Box
                        component="th"
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          bgcolor: 'background.paper',
                          textAlign: 'left',
                          minWidth: 150,
                          px: 1,
                        }}
                      >
                        {en ? 'Item' : 'Пункт'}
                      </Box>
                      {data.students.map((s, i) => (
                        <Box
                          component="th"
                          key={s._id}
                          title={s.name}
                          sx={{
                            px: 0.5,
                            fontWeight: 700,
                            color: colorOf(colorIdx.get(s._id) ?? i),
                            whiteSpace: 'nowrap',
                            maxWidth: 90,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {short(s.name)}
                        </Box>
                      ))}
                      <Box component="th" sx={{ px: 0.5 }}>
                        {en ? 'Group' : 'Группа'}
                      </Box>
                    </tr>
                  </thead>
                  <tbody>
                    {(sortItems === 'order' || allRows
                      ? items
                      : items.slice(0, 40)
                    ).map((it, k, list) => {
                      const showUnit =
                        sortItems === 'order' &&
                        (k === 0 || list[k - 1].unitKey !== it.unitKey);
                      const open =
                        sortItems !== 'order' || unitOpen(it.unitKey);
                      const unitInfo = showUnit
                        ? data.units.find((u) => u.key === it.unitKey)
                        : null;
                      return (
                        <React.Fragment key={`${it.unitKey}-${it.key}`}>
                          {showUnit && (
                            <Box
                              component="tr"
                              onClick={() => toggleUnitRow(it.unitKey)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover td': { filter: 'brightness(0.97)' },
                              }}
                            >
                              <Box
                                component="td"
                                sx={{
                                  pt: 1,
                                  pb: 0.25,
                                  px: 0.5,
                                  fontWeight: 700,
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1,
                                  bgcolor: 'background.paper',
                                  maxWidth: 240,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {unitOpen(it.unitKey) ? '▾ ' : '▸ '}
                                {it.unit}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {' '}
                                  ·{' '}
                                  {
                                    data.items.filter(
                                      (x) => x.unitKey === it.unitKey
                                    ).length
                                  }
                                </Typography>
                              </Box>
                              {data.students.map((s) => {
                                const u = s.units[it.unitKey];
                                return (
                                  <Box
                                    component="td"
                                    key={s._id}
                                    sx={{
                                      pt: 1,
                                      textAlign: 'center',
                                      fontWeight: 800,
                                      color: u
                                        ? masteryTone(u.pct || 0, true, dark)
                                        : 'text.disabled',
                                    }}
                                  >
                                    {u ? `${u.pct}%` : '—'}
                                  </Box>
                                );
                              })}
                              <Box
                                component="td"
                                sx={{
                                  pt: 1,
                                  textAlign: 'right',
                                  px: 0.75,
                                  fontWeight: 800,
                                }}
                              >
                                {unitInfo && unitInfo.collective != null
                                  ? `${unitInfo.collective}%`
                                  : '—'}
                              </Box>
                            </Box>
                          )}
                          {open && (
                            <tr>
                              <Box
                                component="td"
                                title={it.meaning || it.text}
                                sx={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1,
                                  bgcolor: 'background.paper',
                                  px: 1,
                                  maxWidth: 220,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {it.text}
                              </Box>
                              {data.students.map((s) => {
                                const v = it.scores[s._id];
                                const st =
                                  v === null || v === undefined
                                    ? null
                                    : SCORE[v];
                                return (
                                  <Box
                                    component="td"
                                    key={s._id}
                                    title={`${s.name}: ${st ? (en ? st.en : st.ru) : en ? 'not assessed' : 'не оценивали'}`}
                                    sx={{
                                      textAlign: 'center',
                                      borderRadius: '6px',
                                      minWidth: 34,
                                      height: 24,
                                      fontWeight: 700,
                                      bgcolor: st
                                        ? st.bg
                                        : alpha(
                                            theme.palette.text.primary,
                                            0.05
                                          ),
                                      color: st ? st.fg : 'text.disabled',
                                    }}
                                  >
                                    {st ? v : '·'}
                                  </Box>
                                );
                              })}
                              <Box
                                component="td"
                                sx={{
                                  textAlign: 'right',
                                  px: 0.75,
                                  fontWeight: 700,
                                  color: masteryTone(
                                    it.collective || 0,
                                    it.collective != null,
                                    dark
                                  ),
                                }}
                              >
                                {it.collective == null
                                  ? '—'
                                  : `${it.collective}%`}
                              </Box>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Box>
              </Box>
              {sortItems !== 'order' && !allRows && items.length > 40 && (
                <Button
                  size="small"
                  onClick={() => setAllRows(true)}
                  sx={{ mt: 1 }}
                >
                  {en
                    ? `Show all ${items.length}`
                    : `Показать все ${items.length}`}
                </Button>
              )}
              {sortItems === 'order' && data.items.length > 60 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  {en
                    ? 'Tap a unit to see its words.'
                    : 'Нажмите на раздел, чтобы раскрыть его слова.'}
                </Typography>
              )}
              <Box
                sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.25 }}
              >
                {[2, 1, 0].map((v) => (
                  <Box
                    key={v}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '4px',
                        bgcolor: SCORE[v].bg,
                      }}
                    />
                    <Typography variant="caption">{`${v} — ${en ? SCORE[v].en : SCORE[v].ru}`}</Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary">
                  · {en ? 'not assessed' : 'не оценивали'}
                </Typography>
              </Box>
            </Section>
          )}

          {/* ── Over time ── */}
          {historyChart && (
            <Section
              title={en ? 'Progress over time' : 'Рост со временем'}
              sub={
                en
                  ? 'Every re-assessment on the compared units.'
                  : 'Каждая переоценка по выбранным разделам.'
              }
            >
              <Box sx={{ height: 280 }}>{historyChart}</Box>
            </Section>
          )}
        </>
      )}
      {loading && !data && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            placeItems: 'center',
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
